import React, { useState, useRef } from 'react';
import { Box, Input, Button, VStack, Heading, useToast, Text, FormControl, FormLabel, FormErrorMessage, HStack, Image, Divider } from '@chakra-ui/react';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

interface FormErrors {
  password?: string;
  confirmPassword?: string;
}

interface PasswordRule {
  label: string;
  test: (password: string) => boolean;
  met: boolean;
}

const SetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Refs for focus management
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  const token = searchParams.get('token');

  // Password validation rules
  const passwordRules: PasswordRule[] = [
    {
      label: "At least 8 characters",
      test: (pwd) => pwd.length >= 8,
      met: false,
    },
    {
      label: "One uppercase letter (A-Z)",
      test: (pwd) => /[A-Z]/.test(pwd),
      met: false,
    },
    {
      label: "One lowercase letter (a-z)",
      test: (pwd) => /[a-z]/.test(pwd),
      met: false,
    },
    {
      label: "One number (0-9)",
      test: (pwd) => /\d/.test(pwd),
      met: false,
    },
    {
      label: "One special character (@$!%*?&)",
      test: (pwd) => /[@$!%*?&]/.test(pwd),
      met: false,
    },
  ];

  // Update password rules based on current password
  const getPasswordRules = (): PasswordRule[] => {
    return passwordRules.map(rule => ({
      ...rule,
      met: rule.test(password),
    }));
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password.trim()) {
      return 'Password is required';
    }
    
    const rules = getPasswordRules();
    const unmetRules = rules.filter(rule => !rule.met);
    
    if (unmetRules.length > 0) {
      return `Password must meet all requirements`;
    }
    
    return undefined;
  };

  const validateConfirmPassword = (confirmPassword: string): string | undefined => {
    if (!confirmPassword.trim()) {
      return 'Please confirm your password';
    }
    
    if (password !== confirmPassword) {
      return 'Passwords do not match';
    }
    
    return undefined;
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    
    // Clear password error when user types
    if (errors.password) {
      setErrors(prev => ({
        ...prev,
        password: undefined,
      }));
    }
    
    // Re-validate confirm password if it exists
    if (confirmPassword && errors.confirmPassword) {
      const confirmError = validateConfirmPassword(confirmPassword);
      setErrors(prev => ({
        ...prev,
        confirmPassword: confirmError,
      }));
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    
    // Clear confirm password error when user types
    if (errors.confirmPassword) {
      setErrors(prev => ({
        ...prev,
        confirmPassword: undefined,
      }));
    }
  };

  const handleBlur = (field: keyof FormErrors) => {
    if (field === 'password') {
      const error = validatePassword(password);
      if (error) {
        setErrors(prev => ({
          ...prev,
          password: error,
        }));
      }
    } else if (field === 'confirmPassword') {
      const error = validateConfirmPassword(confirmPassword);
      if (error) {
        setErrors(prev => ({
          ...prev,
          confirmPassword: error,
        }));
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    newErrors.password = validatePassword(password);
    newErrors.confirmPassword = validateConfirmPassword(confirmPassword);
    
    setErrors(newErrors);
    
    // Focus on first invalid field
    if (newErrors.password) {
      passwordRef.current?.focus();
      return false;
    }
    if (newErrors.confirmPassword) {
      confirmPasswordRef.current?.focus();
      return false;
    }
    
    return true;
  };

  const handleSetPassword = async () => {
    if (!validateForm()) {
      return;
    }

    if (!token) {
      toast({ 
        title: 'Invalid Link', 
        description: 'This password reset link is invalid or has expired.', 
        status: 'error', 
        duration: 5000, 
        isClosable: true 
      });
      return;
    }

    setLoading(true);
    
    try {
      await authAPI.resetPassword(token, password);
      toast({ 
        title: 'Password Reset Successfully', 
        description: 'Your password has been updated. Please login with your new password.', 
        status: 'success', 
        duration: 5000, 
        isClosable: true 
      });
      navigate('/login');
    } catch (err: any) {
      toast({ 
        title: 'Error', 
        description: err.response?.data?.message || 'Failed to reset password', 
        status: 'error', 
        duration: 5000, 
        isClosable: true 
      });
    } finally {
      setLoading(false);
    }
  };

  const currentPasswordRules = getPasswordRules();

  return (
    <Box minH="100vh" bgGradient="linear(to-br, primary.50, white 60%)" display="flex" alignItems="center" justifyContent="center" p={4}>
      <Box maxW="400px" w="full" bg="white" borderRadius="2xl" boxShadow="2xl" p={8} borderWidth={1} borderColor="primary.100">
        <VStack spacing={6} align="stretch">
          {/* Logo & Title */}
          <VStack spacing={2} textAlign="center">
            <Image src="/favicon.png" alt="CMS Logo" boxSize="48px" mx="auto" />
            <Heading size="lg" color="primary.700">Reset Your Password</Heading>
            <Text color="gray.500" fontSize="md">
              Enter your new password below
            </Text>
          </VStack>
          <Divider />

          {/* Password Reset Form */}
          <VStack spacing={4} align="stretch">
            <FormControl isInvalid={!!errors.password}>
              <FormLabel>New Password</FormLabel>
              <Input
                id="password"
                ref={passwordRef}
                type="password"
                placeholder="Enter your new password"
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                onBlur={() => handleBlur('password')}
                size="lg"
                bg="primary.50"
                borderColor={errors.password ? 'red.500' : 'primary.200'}
                _focus={{ 
                  borderColor: errors.password ? 'red.500' : 'primary.400', 
                  boxShadow: errors.password ? '0 0 0 2px #E53E3E' : '0 0 0 2px #7B4BC1' 
                }}
              />
              <FormErrorMessage>{errors.password}</FormErrorMessage>
            </FormControl>

            {/* Real-time Password Rules */}
            <Box bg="gray.50" p={3} borderRadius="md" border="1px" borderColor="gray.200">
              <Text fontSize="sm" color="gray.700" fontWeight="medium" mb={2}>
                Password Requirements:
              </Text>
              <VStack spacing={1} align="start">
                {currentPasswordRules.map((rule, index) => (
                  <HStack key={index} spacing={2}>
                    {rule.met ? (
                      <CheckIcon color="green.500" boxSize={3} />
                    ) : (
                      <CloseIcon color="red.500" boxSize={3} />
                    )}
                    <Text 
                      fontSize="xs" 
                      color={rule.met ? "green.600" : "red.600"}
                      fontWeight={rule.met ? "medium" : "normal"}
                    >
                      {rule.label}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Box>

            <FormControl isInvalid={!!errors.confirmPassword}>
              <FormLabel>Confirm New Password</FormLabel>
              <Input
                id="confirmPassword"
                ref={confirmPasswordRef}
                type="password"
                placeholder="Confirm your new password"
                value={confirmPassword}
                onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                onBlur={() => handleBlur('confirmPassword')}
                size="lg"
                bg="primary.50"
                borderColor={errors.confirmPassword ? 'red.500' : 'primary.200'}
                _focus={{ 
                  borderColor: errors.confirmPassword ? 'red.500' : 'primary.400', 
                  boxShadow: errors.confirmPassword ? '0 0 0 2px #E53E3E' : '0 0 0 2px #7B4BC1' 
                }}
              />
              <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
            </FormControl>

            <Button
              colorScheme="primary"
              size="lg"
              width="full"
              isLoading={loading}
              loadingText="Resetting..."
              onClick={handleSetPassword}
              fontWeight="bold"
              boxShadow="md"
            >
              Reset Password
            </Button>
          </VStack>

          {/* Back to Login */}
          <HStack justify="center" pt={4}>
            <Text fontSize="sm" color="gray.500">
              Remember your password?{' '}
              <Text as="span" color="primary.500" fontWeight="bold" cursor="pointer" onClick={() => navigate('/login')}>
                Back to Login
              </Text>
            </Text>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
};

export default SetPassword;
